import { Link } from "react-router-dom";
import {
  ArrowRight, Brain, Cloud, ShieldCheck, Atom, GitBranch, Repeat,
  TrendingUp, Users, Scale, HeartPulse, Megaphone,
  Stethoscope, Activity, ClipboardList,
  Rocket, Globe2,
  Zap, MapPin, Layers, Clock,
} from "lucide-react";
import CountUp from "@/components/CountUp";
import CTABand from "@/components/CTABand";

const HERO_IMG = "https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWxzJTIwb2ZmaWNlfGVufDB8fHx8MTc3NzA1NTkwNHww&ixlib=rb-4.1.0&q=85";
const TECH_IMG = "https://images.unsplash.com/photo-1762279389045-110301edeecc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGZ1dHVyaXN0aWMlMjB0ZWNobm9sb2d5JTIwbmV0d29ya3xlbnwwfHx8fDE3NzcwNTU5MDR8MA&ixlib=rb-4.1.0&q=85";
const GEN_IMG = "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80";
const HEALTH_IMG = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1400&q=80";
const CASE_IMG = "https://images.unsplash.com/photo-1643104897460-ab6b868eb59b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx2YW5jb3V2ZXIlMjBza3lsaW5lJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzA1NTkwNHww&ixlib=rb-4.1.0&q=85";

const techPractices = [
  { icon: Brain, title: "AI / ML & Data Engineering", desc: "Generative AI, MLOps, RAG specialists.", color: "var(--accent-purple)" },
  { icon: Cloud, title: "Cloud & Platform", desc: "AWS, Azure, GCP architects and SREs.", color: "var(--accent-teal)" },
  { icon: ShieldCheck, title: "Cybersecurity & GRC", desc: "Zero-trust, SOC, penetration testers.", color: "var(--accent-amber)" },
  { icon: Atom, title: "Quantum & Emerging", desc: "Canada's quantum talent pipeline.", color: "var(--accent-mint)" },
  { icon: GitBranch, title: "DevSecOps & Automation", desc: "Shift-left pipelines, CI/CD architects.", color: "var(--accent-teal)" },
  { icon: Repeat, title: "Digital Transformation", desc: "ERP migrations, product modernization.", color: "var(--accent-purple)" },
];

const generalPractices = [
  { icon: TrendingUp, title: "Finance & Accounting", desc: "CFO-interim, controllers, FP&A.", color: "var(--accent-mint)" },
  { icon: Users, title: "HR & People Ops", desc: "CHRO interim, HRBPs, TA leads.", color: "var(--accent-teal)" },
  { icon: Scale, title: "Legal & Compliance", desc: "In-house counsel, privacy officers.", color: "var(--accent-purple)" },
  { icon: HeartPulse, title: "Healthcare & Life Sciences", desc: "Regulatory (Health Canada), clinical.", color: "var(--accent-coral)" },
  { icon: Megaphone, title: "Sales & Marketing", desc: "Enterprise AEs, RevOps, growth.", color: "var(--accent-purple)" },
];

const healthcarePractices = [
  { icon: Stethoscope, title: "Travel Nursing", desc: "RNs, NPs, LPNs on 8–26 week contracts across all provinces.", color: "var(--accent-coral)" },
  { icon: Activity, title: "Allied Health", desc: "PT, OT, RT, MLT, sonography, pharmacy — fully credentialed.", color: "var(--accent-teal)" },
  { icon: ClipboardList, title: "Non-Clinical Staffing", desc: "HIM, EHR/Epic IT, billing, compliance, clinic leadership.", color: "var(--accent-mint)" },
];

const differentiators = [
  { icon: Zap, title: "Speed without sacrifice", desc: "Shortlists in under 5 business days, every time — backed by a quality guarantee." },
  { icon: MapPin, title: "Canadian compliance-first", desc: "Provincial labour law, T4/T4A, PIPEDA and Law 25 fluency from day one." },
  { icon: Layers, title: "Contract · Perm · SOW · RPO", desc: "Four engagement models. One account team. No hand-offs between lines of business." },
  { icon: Clock, title: "Passive-candidate reach", desc: "GitHub, Kaggle, conference circuits — not just LinkedIn's active applicants." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 pt-12 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="eyebrow mb-5">Canada&apos;s specialist staffing partner</div>
            <h1 className="display-xl">
              From <span className="italic" style={{ color: "var(--accent-purple)" }}>AI/ML engineers</span> to frontline talent —
              <span className="italic" style={{ color: "var(--accent-teal)" }}> delivered fast.</span>
            </h1>
            <p className="mt-6 text-lg text-[color:var(--muted)] max-w-xl leading-relaxed">
              Ashren is a dual-lane staffing firm. A boutique consultancy for niche technology
              hiring. A full-cycle partner for finance, legal, healthcare, and operational roles
              coast-to-coast.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/for-employers" className="btn-primary" data-testid="hero-cta-hire-btn">
                Hire Talent <ArrowRight size={18} />
              </Link>
              <Link to="/for-candidates" className="btn-secondary" data-testid="hero-cta-candidate-btn">
                I&apos;m looking for work
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[color:var(--muted)]">
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent-mint)]" />
                Based in Vancouver, BC
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent-teal)]" />
                Delivering across 5 provinces
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent-amber)]" />
                Permanent · Contract · SOW · RPO
              </div>
            </div>
          </div>
          <div className="md:col-span-5 fade-up delay-1">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, rgba(108,91,232,0.25) 0%, transparent 70%)" }} />
              <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, rgba(29,180,222,0.25) 0%, transparent 70%)" }} />
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[color:var(--border)] shadow-lg">
                <img src={HERO_IMG} alt="Diverse corporate professionals collaborating" className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-5 left-5 bg-white border border-[color:var(--border)] rounded-xl p-4 shadow-md max-w-[220px]">
                <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--accent-purple)]">Avg. time-to-shortlist</div>
                <div className="font-display text-2xl mt-1">96 hours</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-[color:var(--border)] bg-white" data-testid="stats-bar">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: 96, suffix: "h", label: "Avg. time-to-shortlist" },
            { v: 14, suffix: "", label: "Specialist practice areas" },
            { v: 5, suffix: "", label: "Canadian provinces served" },
            { v: 90, suffix: "%", label: "90-day retention rate" },
          ].map((s, i) => (
            <div key={s.label} className="text-center md:text-left" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl md:text-5xl text-[color:var(--primary-dark)]">
                <CountUp end={s.v} suffix={s.suffix} testId={`stat-count-${i}`} />
              </div>
              <div className="text-sm text-[color:var(--muted)] mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TRIPLE-PATH */}
      <section className="py-20 md:py-28" data-testid="dual-path-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-8">
              <div className="eyebrow mb-3">Three specialist lanes</div>
              <h2 className="display-lg">Whichever critical hire is on your desk today — we handle it.</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="card overflow-hidden" data-testid="dual-tech-card">
              <div className="aspect-[16/9] -mx-8 -mt-8 mb-6 overflow-hidden">
                <img src={TECH_IMG} alt="Abstract technology" className="w-full h-full object-cover" />
              </div>
              <div className="eyebrow mb-2" style={{ color: "var(--accent-purple)" }}>Technology Staffing</div>
              <h3 className="display-md">Roles your recruiters can&apos;t fill.</h3>
              <p className="mt-3 text-[color:var(--muted)] leading-relaxed">
                AI/ML, Cloud, Cybersecurity, Quantum, DevSecOps. Pre-vetted passive candidates from
                GitHub, Kaggle, and the conference circuit.
              </p>
              <Link to="/technology-staffing" className="btn-ghost mt-6" data-testid="dual-tech-link">
                Explore tech practices <ArrowRight size={16} />
              </Link>
            </div>

            <div className="card overflow-hidden" data-testid="dual-general-card">
              <div className="aspect-[16/9] -mx-8 -mt-8 mb-6 overflow-hidden">
                <img src={GEN_IMG} alt="Professional business meeting" className="w-full h-full object-cover" />
              </div>
              <div className="eyebrow mb-2" style={{ color: "var(--accent-mint)" }}>General Staffing</div>
              <h3 className="display-md">Every critical business hire.</h3>
              <p className="mt-3 text-[color:var(--muted)] leading-relaxed">
                Finance, HR, legal, life sciences, sales. One firm in place of four — accountable
                SLAs across every line of business.
              </p>
              <Link to="/general-staffing" className="btn-ghost mt-6" data-testid="dual-general-link">
                Explore general practices <ArrowRight size={16} />
              </Link>
            </div>

            <div className="card overflow-hidden" data-testid="dual-healthcare-card">
              <div className="aspect-[16/9] -mx-8 -mt-8 mb-6 overflow-hidden">
                <img src={HEALTH_IMG} alt="Healthcare professionals" className="w-full h-full object-cover" />
              </div>
              <div className="eyebrow mb-2" style={{ color: "var(--accent-coral)" }}>Healthcare Staffing</div>
              <h3 className="display-md">The workforce that keeps care running.</h3>
              <p className="mt-3 text-[color:var(--muted)] leading-relaxed">
                Travel nursing, allied health, and non-clinical operations talent — credentialed,
                travel-coordinated, ready in days.
              </p>
              <Link to="/healthcare-staffing" className="btn-ghost mt-6" data-testid="dual-healthcare-link">
                Explore healthcare practices <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-20 md:py-28 bg-white border-y border-[color:var(--border)]" data-testid="why-ashren-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-6">
              <div className="eyebrow mb-3">Why Ashren</div>
              <h2 className="display-lg">Four reasons Canadian executives pick us first.</h2>
            </div>
            <div className="md:col-span-6 md:pt-2">
              <p className="text-[color:var(--muted)] leading-relaxed text-lg">
                We&apos;re the partner you call when the role has been open for 90 days, when the
                previous vendor missed the SLA, or when your VP of Engineering quits on a Friday.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {differentiators.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={i} className="card h-full" data-testid={`diff-card-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `rgba(29,180,222,0.12)`, color: "var(--accent-teal)" }}>
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl mb-2">{d.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TECH PRACTICE GRID */}
      <section className="py-20 md:py-28" data-testid="tech-practices-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3" style={{ color: "var(--accent-purple)" }}>Niche Technology</div>
              <h2 className="display-lg">Six high-demand practice areas.</h2>
            </div>
            <Link to="/technology-staffing" className="btn-ghost" data-testid="tech-grid-all-link">
              All tech practices <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {techPractices.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="practice-card h-full" style={{ "--accent": p.color }} data-testid={`tech-practice-card-${i}`}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg mb-1">{p.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CASE / QUOTE */}
      <section className="py-20 md:py-28 bg-white border-y border-[color:var(--border)]" data-testid="testimonial-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-[color:var(--border)]">
              <img src={CASE_IMG} alt="Vancouver skyline" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow mb-4">Client impact</div>
            <blockquote className="font-display text-2xl md:text-3xl leading-snug text-[color:var(--primary-dark)]">
              &ldquo;Ashren placed our Head of ML Platform in 11 days after two other firms spent
              four months and delivered nothing. They understood the role in the first call.&rdquo;
            </blockquote>
            <div className="mt-6 text-sm text-[color:var(--muted)]">
              <span className="font-semibold text-[color:var(--primary-dark)]">CTO</span> ·
              VC-backed Series B fintech, Toronto
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { v: "11 days", label: "To hired offer" },
                { v: "$180K", label: "Total comp placement" },
                { v: "24 mo", label: "Retention at post" },
              ].map((m, i) => (
                <div key={m.label} data-testid={`case-metric-${i}`}>
                  <div className="font-display text-2xl">{m.v}</div>
                  <div className="text-xs text-[color:var(--muted)] mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GENERAL GRID */}
      <section className="py-20 md:py-28" data-testid="general-practices-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3" style={{ color: "var(--accent-mint)" }}>General Staffing</div>
              <h2 className="display-lg">The critical hires that keep the business running.</h2>
            </div>
            <Link to="/general-staffing" className="btn-ghost" data-testid="general-grid-all-link">
              All general practices <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {generalPractices.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="practice-card h-full" style={{ "--accent": p.color }} data-testid={`general-practice-card-${i}`}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg mb-1">{p.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HEALTHCARE GRID */}
      <section className="py-20 md:py-28 bg-white border-y border-[color:var(--border)]" data-testid="healthcare-practices-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3" style={{ color: "var(--accent-coral)" }}>Healthcare Staffing</div>
              <h2 className="display-lg">Travel nursing, allied health, and the non-clinical backbone.</h2>
            </div>
            <Link to="/healthcare-staffing" className="btn-ghost" data-testid="healthcare-grid-all-link">
              All healthcare practices <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {healthcarePractices.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="practice-card h-full" style={{ "--accent": p.color }} data-testid={`healthcare-practice-card-${i}`}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-lg mb-1">{p.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKFORCE SOLUTIONS — RPO + EOR */}
      <section className="py-20 md:py-24" data-testid="workforce-solutions-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="eyebrow mb-3" style={{ color: "var(--accent-teal)" }}>Workforce Solutions</div>
              <h2 className="display-lg">Beyond staffing: RPO and EOR for Canada.</h2>
              <p className="mt-4 text-[color:var(--muted)] leading-relaxed max-w-xl">
                When staffing alone isn&apos;t enough — outsource your hiring function, or hire in
                Canada without a legal entity. Two solutions built for scale, compliance, and speed.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="card h-full flex flex-col" data-testid="workforce-rpo-card">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                   style={{ background: "rgba(29,180,222,0.14)", color: "var(--accent-teal)" }}>
                <Rocket size={22} />
              </div>
              <div className="eyebrow mb-2" style={{ color: "var(--accent-teal)" }}>Recruitment Process Outsourcing</div>
              <h3 className="display-md">Your hiring. Our process.</h3>
              <p className="mt-3 text-[color:var(--muted)] leading-relaxed flex-1">
                Dedicated recruiters embedded in your team. Full, Project, or Hybrid RPO — priced
                as a flat monthly managed service, not per-hire agency fees. Bilingual EN/FR, PIPEDA
                compliant, deployed in 5–7 business days.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ background: "rgba(29,180,222,0.12)", color: "var(--accent-teal)" }}>From $4,500 / month</span>
                <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ background: "rgba(15,21,36,0.08)", color: "var(--primary-dark)" }}>3 engagement models</span>
              </div>
              <Link to="/rpo" className="btn-ghost mt-6" data-testid="workforce-rpo-link">
                Explore RPO <ArrowRight size={16} />
              </Link>
            </div>

            <div className="card h-full flex flex-col" data-testid="workforce-eor-card">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                   style={{ background: "rgba(46,204,155,0.14)", color: "var(--accent-mint)" }}>
                <Globe2 size={22} />
              </div>
              <div className="eyebrow mb-2" style={{ color: "var(--accent-mint)" }}>Employer of Record — Canada</div>
              <h3 className="display-md">Hire anyone in Canada. We handle the rest.</h3>
              <p className="mt-3 text-[color:var(--muted)] leading-relaxed flex-1">
                Legally employ workers across all 10 provinces and 3 territories — no Canadian
                entity required. Payroll, CPP/EI, T4, WCB, bilingual Quebec contracts, and
                compliance managed end-to-end. First hire live in 3–5 business days.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ background: "rgba(46,204,155,0.12)", color: "var(--accent-mint)" }}>From 8–12% of salary</span>
                <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ background: "rgba(15,21,36,0.08)", color: "var(--primary-dark)" }}>Pan-Canadian</span>
              </div>
              <Link to="/eor" className="btn-ghost mt-6" data-testid="workforce-eor-link">
                Explore EOR <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RESOURCES TEASER */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="resources-teaser">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-10">
            <div className="md:col-span-7">
              <div className="eyebrow mb-3">Free resources</div>
              <h2 className="display-lg">Built for Canadian hiring managers.</h2>
            </div>
            <div className="md:col-span-5 md:pt-2">
              <p className="text-[color:var(--muted)] leading-relaxed">
                Practical tools used by CTOs, COOs, and procurement leaders across Canadian
                scaleups. Gated by email — delivered instantly.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { tag: "Guide · 2025", title: "Canada Tech Salary Guide", color: "var(--accent-purple)" },
              { tag: "Diagnostic · 15 Q", title: "AI Talent Readiness Assessment", color: "var(--accent-teal)" },
              { tag: "Checklist · 32 pts", title: "Staffing Vendor Audit Checklist", color: "var(--accent-amber)" },
            ].map((r, i) => (
              <Link to="/resources" key={r.title} className="practice-card h-full block" style={{ "--accent": r.color }} data-testid={`resource-teaser-${i}`}>
                <div className="eyebrow mb-6" style={{ color: r.color }}>{r.tag}</div>
                <h3 className="font-display text-xl mb-8">{r.title}</h3>
                <div className="flex items-center gap-1 text-sm font-medium">
                  Download <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Tell us the role. We'll tell you exactly who you need."
        subtitle="A 20-minute discovery call with a senior consultant — no pitch deck, no pressure. Just a clear read on the market and what it takes to close."
        primaryLabel="Request a talent brief"
        primaryTo="/for-employers"
        secondaryLabel="Contact us"
        secondaryTo="/contact"
      />
    </div>
  );
}
